import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';

// Icons
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import PolicyIcon from '@mui/icons-material/Policy';
import PublicIcon from '@mui/icons-material/Public';
import DeleteIcon from '@mui/icons-material/Delete';
import UpdateIcon from '@mui/icons-material/Update';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

const Privacy = () => {
  const lastUpdated = "March 25, 2025";

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 4 }}>
          <AlertTitle>Your Privacy Matters</AlertTitle>
          This Privacy Policy explains how AI This Job collects, uses, and protects your information when you use our service. We are committed to safeguarding your privacy while providing effective AI automation tools.
        </Alert>

        <Typography paragraph>
          At AI This Job, we are committed to protecting your privacy and ensuring the security of your information. This Privacy Policy outlines our practices regarding the collection, use, and disclosure of your information when you use our Service, and informs you about your privacy rights and how the law protects you.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          1. Information We Collect
        </Typography>
        
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Personal Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              We collect personal information that you voluntarily provide to us when you register for the Service, express interest in obtaining information about us or our products, or otherwise contact us. This personal information may include:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Name, email address, and contact information" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Account credentials such as usernames and passwords" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Billing information (if applicable)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile preferences and settings" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Service Data</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              When you use our Service, we collect information that you provide as part of the functionality:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Task descriptions, prompts, and instructions you create" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Automation workflows and configurations" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="API keys for third-party services (which are encrypted)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Task execution logs and performance metrics" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Automatically Collected Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              When you access or use our Service, we automatically collect certain information:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Device information (e.g., IP address, browser type, operating system)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Usage data (e.g., pages visited, features used, time spent)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Performance data (e.g., load times, errors)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Cookies and similar tracking technologies" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          2. How We Use Your Information
        </Typography>
        <Typography paragraph>
          We use the information we collect for various purposes:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <SecurityIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Provide, maintain, and improve our Service" 
              secondary="Including processing your tasks, operating your automations, and delivering requested features"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LockIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Manage your account and provide customer support" 
              secondary="Including responding to your inquiries and resolving issues"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <UpdateIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Send important notifications and updates" 
              secondary="Including service-related announcements and security alerts"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <InfoIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Improve and develop our Service" 
              secondary="Including analyzing usage patterns, diagnosing technical issues, and enhancing user experience"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <PolicyIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Ensure security and legal compliance" 
              secondary="Including detecting and preventing fraudulent activity and enforcing our Terms of Service"
            />
          </ListItem>
        </List>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          3. Data Sharing and Disclosure
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle><strong>Our Commitment</strong></AlertTitle>
          <Typography paragraph sx={{ mt: 1 }}>
            <strong>We do not sell your personal data to third parties under any circumstances.</strong>
          </Typography>
        </Alert>
        
        <Typography paragraph>
          However, we may share your information in the following limited circumstances:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemIcon>
              <GroupIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Service Providers" 
              secondary="We may share information with trusted third-party service providers who assist us in operating our Service, conducting our business, or servicing you. These providers have access to your information only to perform specific tasks on our behalf and are obligated to protect your information."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <PublicIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="AI Technology Partners" 
              secondary="When you use our Service to interact with AI systems (like Claude), your prompts and instructions are processed by these AI systems in accordance with their privacy practices. We have agreements in place that require these partners to handle your data appropriately."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <PolicyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Legal Requirements" 
              secondary="We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency). We may also disclose information to protect our rights, privacy, safety, or property, or that of our users or others."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <StorageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Business Transfers" 
              secondary="If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on our website of any change in ownership or uses of your information."
            />
          </ListItem>
        </List>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          4. Data Security
        </Typography>
        <Typography paragraph>
          We implement appropriate technical and organizational measures to protect your information:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <LockIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Encryption of sensitive data, including API keys" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LockIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Secure infrastructure with industry-standard protections" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LockIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Regular security assessments and updates" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LockIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Access controls limiting who can view your data" />
          </ListItem>
        </List>
        <Typography paragraph sx={{ mt: 2 }}>
          While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or method of electronic storage is 100% secure. We cannot guarantee absolute security of your data.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          5. Data Retention
        </Typography>
        <Typography paragraph>
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, including satisfying any legal, accounting, or reporting requirements. We determine the appropriate retention period based on the amount, nature, and sensitivity of the data, the potential risk of harm from unauthorized use or disclosure, and applicable legal requirements.
        </Typography>
        <Typography paragraph>
          When we no longer need your personal information, we will securely delete or anonymize it. If anonymization is not possible (for example, because your information has been stored in backup archives), we will securely store your information and isolate it from any further processing until deletion is possible.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          6. Your Privacy Rights
        </Typography>
        <Typography paragraph>
          Depending on your location, you may have certain rights regarding your personal information:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Right to Access" 
              secondary="You can request access to the personal information we hold about you"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <UpdateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Right to Rectification" 
              secondary="You can request that we correct inaccurate or incomplete information"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Right to Erasure" 
              secondary="You can request that we delete your personal information in certain circumstances"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Right to Restrict Processing" 
              secondary="You can request that we restrict the processing of your information in certain circumstances"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ContactMailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Right to Object" 
              secondary="You can object to our processing of your information based on our legitimate interests"
            />
          </ListItem>
        </List>
        <Typography paragraph sx={{ mt: 2 }}>
          To exercise these rights, please contact us using the information provided in the "Contact Us" section. We may request specific information to confirm your identity and ensure your right to access your personal data or to exercise any of your other rights.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          7. Children's Privacy
        </Typography>
        <Typography paragraph>
          Our Service is not intended for individuals under the age of 16. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16 without verification of parental consent, we will take steps to remove that information from our servers.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          8. International Transfers
        </Typography>
        <Typography paragraph>
          Your information may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction. If you are located outside the United States and choose to provide information to us, please note that we transfer the data to the United States and process it there.
        </Typography>
        <Typography paragraph>
          When we transfer personal information to countries outside the EEA, we ensure that a similar degree of protection is afforded to it by ensuring at least one of the following safeguards is implemented:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="We will only transfer your personal information to countries that have been deemed to provide an adequate level of protection for personal information." />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Where we use certain service providers, we may use specific contracts approved for use in the EU which give personal information the same protection it has in Europe." />
          </ListItem>
        </List>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          9. Changes to This Privacy Policy
        </Typography>
        <Typography paragraph>
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
        </Typography>
        <Typography paragraph>
          For significant changes, we will provide a more prominent notice, which may include sending you an email notification if we have your contact information.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          10. Contact Us
        </Typography>
        <Typography paragraph>
          If you have any questions about this Privacy Policy or our data practices, please contact us:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <ContactMailIcon />
            </ListItemIcon>
            <ListItemText primary="By email: privacy@aithisjob.com" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ContactMailIcon />
            </ListItemIcon>
            <ListItemText primary="By mail: AI This Job, Melbourne, FL" />
          </ListItem>
        </List>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            Your Consent
          </Typography>
          <Typography paragraph>
            By using our Service, you consent to our Privacy Policy and agree to its terms. If you do not agree with this policy, please do not use our Service.
          </Typography>
          <Typography>
            This Privacy Policy should be read in conjunction with our <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</Link>.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Privacy;